import { destructureImpactScore } from '@src/utility/impactScore';
import { useMemo, useState } from 'react';

import type { ClipFragment } from '@src/services/AIClipProducer';

export type SortMode = 'impact_score' | 'start_time';

export type ClipLimit = 6 | 9 | 12 | 15 | 'all';

export const CLIP_LIMIT_OPTIONS: { value: ClipLimit; label: string }[] = [
  { value: 6, label: 'Top 6' },
  { value: 9, label: 'Top 9' },
  { value: 12, label: 'Top 12' },
  { value: 15, label: 'Top 15' },
  { value: 'all', label: 'Show All' },
];

/**
 * Extracts the main impact score value for sorting comparison.
 * Returns -Infinity for clips without scores so they sort to the end.
 */
function extractMainImpactScore(
  impactScore: ClipFragment['impactScore'],
): number {
  if (!impactScore || Object.keys(impactScore).length === 0) {
    return -Infinity;
  }
  const { main } = destructureImpactScore(impactScore);
  return main ?? -Infinity;
}

/**
 * Generates a unique key for a clip based on its content.
 * Used for selection tracking that persists across sort order changes.
 */
export function getClipKey(clip: ClipFragment): string {
  return `${clip.startTime}-${clip.transcriptPosition.startIndex}-${clip.transcriptPosition.endIndex}`;
}

/**
 * PLAT-11093 — the selected clips in the order they were picked, which is the
 * running order of a reel combined from them (PLAT-11048 DE11). Reads the
 * selection rather than filtering the results, since the results carry the
 * order the producer generated them in, not the creator's.
 *
 * A selected key with no fragment is dropped: a re-produce replaces the
 * results, and a reel is better one clip short than not saved at all.
 */
export function clipsInSelectionOrder(
  clips: ClipFragment[],
  selectedKeys: ReadonlySet<string>,
): ClipFragment[] {
  const byKey = new Map(clips.map(clip => [getClipKey(clip), clip]));
  return Array.from(selectedKeys)
    .map(key => byKey.get(key))
    .filter((clip): clip is ClipFragment => !!clip);
}

/**
 * Hook for sorting and limiting clip fragments.
 * - Default sort is by impact score (highest first)
 * - Default limit is Top 6, unless `initialLimit` says otherwise
 */
export function useClipSort(
  clips: ClipFragment[],
  initialLimit: ClipLimit = 6,
) {
  const [sortMode, setSortMode] = useState<SortMode>('impact_score');
  const [clipLimit, setClipLimit] = useState<ClipLimit>(initialLimit);

  const sortedClips = useMemo(() => {
    if (!clips || clips.length === 0) return clips;

    const sorted = [...clips].sort((a, b) => {
      if (sortMode === 'impact_score') {
        const scoreA = extractMainImpactScore(a.impactScore);
        const scoreB = extractMainImpactScore(b.impactScore);
        // Higher scores first
        return scoreB - scoreA;
      }
      // Chronological order (earliest first)
      return a.startTime - b.startTime;
    });

    // Apply limit
    if (clipLimit === 'all') return sorted;
    return sorted.slice(0, clipLimit);
  }, [clips, sortMode, clipLimit]);

  const totalClips = clips?.length ?? 0;

  return {
    sortedClips,
    sortMode,
    setSortMode,
    clipLimit,
    setClipLimit,
    totalClips,
  };
}
